/**
 * The bundled fallback theme (§7.3: "fall back to a bundled default on failure").
 *
 * Kept byte-identical in intent to the backend's <c>DefaultTheme.cs</c> — including
 * `primary: #17805F` rather than the `#1F9D77` printed in §7, because white on
 * #1F9D77 measures 3.42:1 and fails WCAG AA for normal text. The API's validator
 * rejects that palette, so shipping it here as a fallback would mean the offline
 * fallback renders buttons the online theme is not allowed to.
 *
 * ⚠️ This is the ONLY place in the admin panel where a colour literal is allowed
 * (§5.7). Everywhere else reads a CSS variable.
 */
export type ThemeTokens = {
  version: number;
  name: string;

  /** The palette. The dark one — §7 was written when there was only one. */
  colors: Record<string, string>;

  /**
   * The daylight palette, when the theme carries one.
   *
   * ⚠️ Optional, and its absence is a real answer. A theme without it simply does not offer
   * daylight, and the panel stays dark rather than inventing a palette by inverting this one —
   * §7's tokens are semantic, and an inverted `danger` is a colour nobody chose and no contrast
   * rule has ever checked.
   */
  colorsLight?: Record<string, string>;
  typography: { fontFamily: string; scale: Record<string, number> };
  radius: Record<string, number>;
  spacing: Record<string, number>;
  assets: Record<string, string>;
};

export const DEFAULT_THEME: ThemeTokens = {
  version: 1,
  name: 'Aoun Default',
  colors: {
    background: '#0B1220',
    surface: '#131C2E',
    surfaceElevated: '#1B2740',
    border: '#28344C',
    primary: '#17805F',
    primaryForeground: '#FFFFFF',
    secondary: '#2E4A8A',
    foreground: '#E8EDF5',
    mutedForeground: '#93A1B8',
    success: '#25A55A',
    warning: '#D8A014',
    danger: '#D6453D',
    info: '#2E7FD8',
    missionMissing: '#D6453D',
    missionStranded: '#D8A014',
    trackLine: '#1F9D77',
    coverageFill: '#1F9D7733',
    gapFill: '#D6453D33',
  },

  // ⚠️ Daylight, chosen against the same WCAG gate — not the dark palette lightened.
  //
  // §6 says it about the map and it is true of the whole interface: dark is "nearly unreadable
  // under midday sun, which is when most searching happens". Three of these are a different hue
  // rather than a lighter one — the §7 amber measures 1.86:1 on white, so warning and
  // missionStranded became #7A5300. Kept identical to the backend's DefaultTheme.cs.
  colorsLight: {
    background: '#F6F8FB',
    surface: '#FFFFFF',
    surfaceElevated: '#EDF1F7',
    border: '#D5DDE9',
    primary: '#0F6B4F',
    primaryForeground: '#FFFFFF',
    secondary: '#24407A',
    foreground: '#0F1729',
    mutedForeground: '#586780',
    success: '#12703E',
    warning: '#7A5300',
    danger: '#B4302A',
    info: '#14548F',
    missionMissing: '#B4302A',
    missionStranded: '#7A5300',
    trackLine: '#0F6B4F',
    coverageFill: '#0F6B4F33',
    gapFill: '#B4302A33',
  },
  typography: {
    fontFamily: 'IBM Plex Sans Arabic',
    scale: { xs: 12, sm: 14, base: 16, lg: 18, xl: 22, '2xl': 28, '3xl': 34 },
  },
  radius: { sm: 6, md: 10, lg: 16, full: 999 },
  spacing: { unit: 4 },
  assets: { logoKey: 'branding/logo.svg', certificateSealKey: 'branding/seal.png' },
};
